// Material exchange for one bristle-group stamp: the per-cell part of the
// brush model. This is the CPU reference for the GPU brush kernel
// (gpu/shaders/brush.wgsl, stampExchange) and must stay semantically equal to
// it. Every footprint cell is treated as independent within a stamp:
//   1. contact and weights are computed per cell from the stamp and surface;
//   2. the belly feeds the tip (release per unit length);
//   3. pickup: each cell offers a fraction of its mobile paint; the total is
//      scaled to the tip's free capacity (one snapshot, no cell order);
//   4. deposit: each cell receives its weight share of one tip snapshot;
//   5. the tip returns a little to the belly; stiff bristles plough.
// Order independence within a stamp is what lets the GPU process a
// footprint in parallel and still match this reference.

import { PIGMENT_SLOTS, markRect } from '../core/PaintState.js';
import { transferCarrier, displaceBody } from '../physics/PaintDeposition.js';
import { BODY_PRESENT } from '../physics/ViscousPaint.js';

export const LOAD_REACH = 12;
export const TIP_FRACTION = 0.06;
export const TIP_EXCHANGE_LENGTH = 0.8;
export const TIP_BACKFLOW = 0.03;

// Stamp record layout (shared with the GPU brush kernel; Float32 per field).
// 3D surface painting: 'island' (0 = none) restricts a stamp to the cells of one
// UV island; 'cover' (0 = whole stamp) is this island copy's share of the
// stamp's exchange (belly feed, release, backflow) when a seam splits it.
export const STAMP_FIELDS = ['group', 'px', 'py', 'baseRadius', 'sponge', 'edgeContact', 'pe', 'ds', 'speedSkip', 'acrossX', 'acrossY', 'dirX', 'dirY', 'island', 'cover'];
export const STAMP_STRIDE = 16;

const smoothstep = (a, b, x) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

const cells = [];
const weights = [];
const takeWater = [];
const takeBody = [];

// params: { radius, release, stiffness, plough, pickupScale, tooth }
export function exchangeStamp(state, params, belly, tip, stamp) {
  const { width, height, water, bodyVol, bodyOpen } = state;
  const relief = state.surface.heightField;
  const { px, py, ds, edgeContact, pe, speedSkip } = stamp;
  const fullness = Math.min(1, (tip.water + tip.body) / Math.max(1e-6, tip.capacity));
  const liquid = tip.water / Math.max(1e-9, tip.water + tip.body);
  const r = stamp.sponge > 0
    ? stamp.baseRadius
    : stamp.baseRadius * (1 + 1.3 * liquid * Math.min(1, fullness * 3) * (1 - params.stiffness * 0.5));
  const cx0 = Math.max(0, Math.floor(px - r));
  const cy0 = Math.max(0, Math.floor(py - r));
  const cx1 = Math.min(width - 1, Math.ceil(px + r));
  const cy1 = Math.min(height - 1, Math.ceil(py + r));
  if (cx0 > cx1 || cy0 > cy1) return 0;
  const r2 = r * r;
  const reach = pe * 1.3 + fullness * 0.7 * (1 - params.stiffness * 0.3);

  // 1. Footprint and contact.
  let count = 0;
  let weightSum = 0;
  let contactSum = 0;
  for (let y = cy0; y <= cy1; y += 1) {
    for (let x = cx0; x <= cx1; x += 1) {
      const ddx = x + 0.5 - px;
      const ddy = y + 0.5 - py;
      const d2 = ddx * ddx + ddy * ddy;
      if (d2 >= r2) continue;
      const i = y * width + x;
      const falloff = (1 - d2 / r2) ** 2;
      weightSum += falloff;
      const inPaint = bodyVol[i] > 0.05 || water[i] > 0.05;
      const depth = (1 - relief[i]) * params.tooth;
      const contact = inPaint ? edgeContact : edgeContact * smoothstep(depth - 0.15, depth + 0.2, reach) * speedSkip;
      if (contact <= 0.005) continue;
      cells[count] = i;
      weights[count] = falloff * contact;
      contactSum += falloff * contact;
      count += 1;
    }
  }
  if (count === 0) return 0;
  markRect(state, cx0 - 2, cy0 - 2, cx1 + 2, cy1 + 2, true);

  // 2. Belly feeds the tip.
  const contactShare = Math.min(1, contactSum / weightSum);
  transferCarrier(belly, tip, Math.min(0.9, (params.release * ds * contactShare) / (LOAD_REACH * params.radius)));

  // 3. Pickup, scaled to the tip's free room.
  let wantTotal = 0;
  for (let c = 0; c < count; c += 1) {
    const i = cells[c];
    const f = Math.min(1, params.pickupScale * weights[c] * ds * 0.25);
    const h = water[i];
    const v = bodyVol[i] > BODY_PRESENT ? bodyVol[i] * bodyOpen[i] : 0;
    takeWater[c] = h * f;
    takeBody[c] = v * f;
    wantTotal += takeWater[c] + takeBody[c];
  }
  const room = Math.max(0, tip.capacity - tip.water - tip.body);
  const pickScale = wantTotal > room ? room / wantTotal : 1;
  if (wantTotal > 1e-12 && pickScale > 0) {
    for (let c = 0; c < count; c += 1) {
      const i = cells[c];
      const tw = takeWater[c] * pickScale;
      if (tw > 0 && water[i] > 0) {
        const fw = tw / water[i];
        water[i] -= tw;
        tip.water += tw;
        for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
          const a = state.wetPig[k][i] * fw;
          state.wetPig[k][i] -= a;
          tip.pigW[k] += a;
        }
      }
      const tb = takeBody[c] * pickScale;
      if (tb > 0 && bodyVol[i] > 0) {
        const fb = tb / bodyVol[i];
        bodyVol[i] -= tb;
        tip.body += tb;
        for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
          const a = state.bodyPig[k][i] * fb;
          state.bodyPig[k][i] -= a;
          tip.pigB[k] += a;
        }
      }
    }
  }

  // 4. Deposit shares of one tip snapshot.
  const tipRelease = Math.min(0.9, ds / (TIP_EXCHANGE_LENGTH * params.radius));
  const snapWater = tip.water;
  const snapBody = tip.body;
  const snapW = Float32Array.from(tip.pigW);
  const snapB = Float32Array.from(tip.pigB);
  const m = tip.medium;
  let releasedFraction = 0;
  for (let c = 0; c < count; c += 1) {
    const i = cells[c];
    const frac = (tipRelease * weights[c]) / weightSum;
    releasedFraction += frac;
    const w = snapWater * frac;
    if (w > 0) {
      water[i] += w;
      state.depRewet[i] += (m.rewet - state.depRewet[i]) * Math.min(1, w / (water[i] + 1e-9));
    }
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) state.wetPig[k][i] += snapW[k] * frac;
    const b = snapBody * frac;
    if (b <= 1e-9) {
      for (let k = 0; k < PIGMENT_SLOTS; k += 1) state.wetPig[k][i] += snapB[k] * frac;
    } else {
      const after = bodyVol[i] + b;
      const wNew = b / after;
      state.bodyMob[i] += (m.mobility - state.bodyMob[i]) * wNew;
      state.bodyYield[i] += (m.yieldStress - state.bodyYield[i]) * wNew;
      state.bodyDry[i] += (m.bodyDrying - state.bodyDry[i]) * wNew;
      state.bodySolids[i] += (m.heightGain - state.bodySolids[i]) * wNew;
      state.bodyGloss[i] += (m.wetGloss - state.bodyGloss[i]) * wNew;
      bodyOpen[i] += (1 - bodyOpen[i]) * wNew;
      bodyVol[i] = after;
      for (let k = 0; k < PIGMENT_SLOTS; k += 1) state.bodyPig[k][i] += snapB[k] * frac;
    }
  }
  const keep = 1 - releasedFraction;
  tip.water = snapWater * keep;
  tip.body = snapBody * keep;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    tip.pigW[k] = snapW[k] * keep;
    tip.pigB[k] = snapB[k] * keep;
  }

  // 5. Backflow to the belly, then ploughing.
  transferCarrier(tip, belly, Math.min(0.5, (TIP_BACKFLOW * ds) / params.radius));
  if (params.plough > 0) {
    const xi = Math.floor(px);
    const yi = Math.floor(py);
    if (xi > 0 && yi > 0 && xi < width - 1 && yi < height - 1) {
      const i = yi * width + xi;
      if (bodyVol[i] > BODY_PRESENT) {
        const f = params.plough * edgeContact * ds * 0.18;
        const sx = Math.round(stamp.acrossX);
        const sy = Math.round(stamp.acrossY);
        const fxi = Math.round(stamp.dirX);
        const fyi = Math.round(stamp.dirY);
        displaceBody(state, i, i + sy * width + sx, f * 0.4);
        displaceBody(state, i, i - sy * width - sx, f * 0.4);
        displaceBody(state, i, i + fyi * width + fxi, f * 0.2);
      }
    }
  }
  return count;
}

export function writeStampRecord(target, offset, stamp) {
  for (let f = 0; f < STAMP_FIELDS.length; f += 1) target[offset + f] = stamp[STAMP_FIELDS[f]] || 0;
}
